import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Analytics } from "@/lib/analyticsShim";

const CartContext = createContext(null);
const LEGACY_KEY = "rm_cart";
const GUEST_KEY = "rm_cart_guest";

function getUserCartKey(userId) {
  return `rm_cart_user_${userId}`;
}

function readCart(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    // Remove legacy shared cart key to prevent cross-account cart leakage.
    if (localStorage.getItem(LEGACY_KEY)) {
      localStorage.removeItem(LEGACY_KEY);
    }
  }, []);

  useEffect(() => {
    const key = user?.id ? getUserCartKey(user.id) : GUEST_KEY;
    setItems(readCart(key));
  }, [user?.id]);

  useEffect(() => {
    const key = user?.id ? getUserCartKey(user.id) : GUEST_KEY;
    localStorage.setItem(key, JSON.stringify(items));
  }, [items, user?.id]);

  const addItem = (item) => {
    const cartItem = { ...item, _cartId: crypto.randomUUID() };
    setItems((prev) => [...prev, cartItem]);
    if (user?.role === "contractor") {
      api.post("/cart/additions", item).catch(() => {});
    }
  };

  const removeItem = (cartId) => {
    setItems((prev) => prev.filter((i) => i._cartId !== cartId));
  };

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const gst = Math.round(subtotal * 0.18 * 100) / 100;
  const total = Math.round((subtotal + gst) * 100) / 100;

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, clear, subtotal, gst, total }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
